import {z} from 'zod';

export const projectModules = z.enum([
  'preImage',
  'sfm',
  'postImage',
  'alignment',
  'objectDetection',
  'autoDetection',
  'heatmap',
  'layerSegmentation',
]);
export const criticalProcessingModules = projectModules.extract([
  'preImage',
  'sfm',
  'postImage',
]);
export const postProcessingModules = projectModules.extract([
  'objectDetection',
  'autoDetection',
  'alignment',
]);
export const onDemandModules = projectModules.extract([
  'heatmap',
  'layerSegmentation',
]);
const rawProjectModuleStatus = z.enum([
  'pending',
  'disabled',
  'processing',
  'success',
  'error',
  'held',
  'queued',
  'stale',
]);
export const projectModuleStatus = rawProjectModuleStatus.exclude([
  'held',
  'queued',
]);

export const projectStatus = rawProjectModuleStatus.exclude([
  'disabled',
  'held',
  'queued',
]);

const projectModuleStatusSchema = z
  .discriminatedUnion('status', [
    z.object({
      id: projectModules,
      status: z.literal(rawProjectModuleStatus.enum.pending),
      metadata: z.record(z.string(), z.string()).nullable(),
    }),
    z.object({
      id: projectModules,
      status: z.literal(rawProjectModuleStatus.enum.disabled),
      metadata: z.record(z.string(), z.string()).nullable(),
    }),
    z.object({
      id: projectModules,
      status: z.literal(rawProjectModuleStatus.enum.processing),
      start: z.coerce.date(),
      metadata: z.record(z.string(), z.string()).nullable(),
    }),
    z.object({
      id: projectModules,
      status: z.literal(rawProjectModuleStatus.enum.held),
      start: z.coerce.date(),
      metadata: z.record(z.string(), z.string()).nullable(),
    }),
    z.object({
      id: projectModules,
      status: z.literal(rawProjectModuleStatus.enum.queued),
      start: z.coerce.date(),
      metadata: z.record(z.string(), z.string()).nullable(),
    }),
    z.object({
      id: projectModules,
      status: z.literal(rawProjectModuleStatus.enum.success),
      start: z.coerce.date(),
      end: z.coerce.date(),
      metadata: z.record(z.string(), z.string()).nullable(),
    }),
    z.object({
      id: projectModules,
      status: z.literal(rawProjectModuleStatus.enum.error),
      start: z.coerce.date(),
      end: z.coerce.date(),
      metadata: z.record(z.string(), z.string()).nullable(),
    }),
    z.object({
      id: projectModules,
      status: z.literal(rawProjectModuleStatus.enum.stale),
      staleSince: z.coerce.date(),
      metadata: z.record(z.string(), z.string()).nullable(),
    }),
  ])
  .transform((data) => {
    if (data.status === 'held' || data.status === 'queued') {
      return {...data, status: 'processing'};
    }

    return data;
  });

export const projectStatusesSchema = projectModuleStatusSchema.array().min(1);

type ModuleStatus = z.infer<typeof projectModuleStatus>;

export function deriveCriticalStatus(
  statuses: z.infer<typeof projectStatusesSchema>,
  isCritical: (id: z.infer<typeof projectModules>) => boolean = isCriticalProcessingModule
): z.infer<typeof projectStatus> {
  const criticalStatuses = statuses.filter(
    ({id, status}) => isCritical(id) && status !== 'disabled'
  );

  if (criticalStatuses.length === 0) {
    throw new Error('All critical processing modules are disabled');
  }

  const priority = ['error', 'processing', 'pending', 'success'];

  const status = criticalStatuses.reduce<ModuleStatus>((acc, {status}) => {
    const currentStatus = priority.indexOf(status);
    const previousStatus = priority.indexOf(acc);
    return currentStatus < previousStatus ? (status as ModuleStatus) : acc;
  }, 'success' as ModuleStatus);

  if (status === 'disabled') throw new Error('All critical processing modules are disabled');

  return status;
}

export function projectStatusTransform<
  T extends {
    statuses: z.infer<typeof projectStatusesSchema>;
    isCompleted: boolean;
  },
>(data: T): T & {status: z.infer<typeof projectStatus>} {
  return {...data, status: deriveCriticalStatus(data.statuses)};
}

export function isImagesetCriticalModule(
  moduleId: z.infer<typeof projectModules>
): boolean {
  return moduleId === 'preImage' || moduleId === 'postImage' || moduleId === 'alignment';
}

export function isCriticalProcessingModule(
  moduleId: z.infer<typeof projectModules>
): moduleId is z.infer<typeof criticalProcessingModules> {
  return moduleId === 'preImage' || moduleId === 'sfm' || moduleId === 'postImage';
}

export function isPostProcessingModule(
  moduleId: z.infer<typeof projectModules>
): moduleId is z.infer<typeof postProcessingModules> {
  return (
    moduleId === 'objectDetection' ||
    moduleId === 'autoDetection' ||
    moduleId === 'alignment'
  );
}

export function isOnDemandModule(
  moduleId: z.infer<typeof projectModules>
): moduleId is z.infer<typeof onDemandModules> {
  return moduleId === 'heatmap' || moduleId === 'layerSegmentation';
}
